// src/components/Book.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./Book.css";

const CATEGORIES = [
  { value: "Academic/University", label: "Academic / University" },
  { value: "Professional", label: "Professional" },
  { value: "Master and PhD Student", label: "Master's and PhD Student" },
];

const MAX_PER_CATEGORY = 30;
const MAX_RECEIPTS = 10;
const MAX_RECEIPT_SIZE_MB = 20;
const MAX_TOTAL_RECEIPT_SIZE_MB = 24;
const MAX_TOTAL_RECEIPT_SIZE_BYTES = MAX_TOTAL_RECEIPT_SIZE_MB * 1024 * 1024;
const ACCEPTED_RECEIPT_MIME = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];

const BATCH_URL = "https://us-central1-veted-africa.cloudfunctions.net/sendBatchRegistrationsHttp";

const emptyAttendee = (userType) => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  userType,
});

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const Book = () => {
  const patternRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Modal + wizard state
  const [isModalOpen, setIsModalOpen] = useState(false);
  // step 0 = counts, 1..N = per person, N+1 = receipts, N+2 = results
  const [step, setStep] = useState(0);
  const [counts, setCounts] = useState({
    "Academic/University": 0,
    Professional: 0,
    "Master and PhD Student": 0,
  });
  const [attendees, setAttendees] = useState([]);
  const [receipts, setReceipts] = useState([]); // File[]
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // { results: [...] }
  const [retryingIndex, setRetryingIndex] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.5 }
    );
    if (patternRef.current) observer.observe(patternRef.current);
    return () => {
      if (patternRef.current) observer.unobserve(patternRef.current);
    };
  }, []);

  const totalPeople = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts]
  );

  // Total wizard steps: counts (1) + per-person (totalPeople) + receipts (1) = 2 + totalPeople
  // step indices: 0 (counts), 1..totalPeople (per-person), totalPeople+1 (receipts), totalPeople+2 (results)
  const totalWizardSteps = 2 + totalPeople;
  const isCountsStep = step === 0;
  const isReceiptsStep = step === totalPeople + 1;
  const isResultsStep = step === totalPeople + 2;
  const isPersonStep = !isCountsStep && !isReceiptsStep && !isResultsStep;
  const currentPersonIndex = isPersonStep ? step - 1 : -1;

  const openModal = () => {
    setIsModalOpen(true);
    setStep(0);
    setCounts({
      "Academic/University": 0,
      Professional: 0,
      "Master and PhD Student": 0,
    });
    setAttendees([]);
    setReceipts([]);
    setErrors({});
    setResults(null);
  };

  const hasProgress = () =>
    totalPeople > 0 ||
    attendees.some((a) => a.firstName || a.lastName || a.email || a.phone) ||
    receipts.length > 0;

  const requestClose = () => {
    if (isResultsStep) {
      setIsModalOpen(false);
      return;
    }
    if (hasProgress()) {
      const ok = window.confirm(
        "Are you sure you want to close? Your registration progress will be lost."
      );
      if (!ok) return;
    }
    setIsModalOpen(false);
  };

  // Build the attendees list when leaving the counts step
  const buildAttendees = () => {
    const list = [];
    CATEGORIES.forEach(({ value }) => {
      for (let i = 0; i < counts[value]; i++) {
        list.push(emptyAttendee(value));
      }
    });
    // Preserve previously filled data when going back and forward
    const merged = list.map((tpl, idx) => attendees[idx] && attendees[idx].userType === tpl.userType
      ? attendees[idx]
      : tpl);
    setAttendees(merged);
  };

  const handleCountChange = (cat, raw) => {
    let v = parseInt(raw, 10);
    if (isNaN(v) || v < 0) v = 0;
    if (v > MAX_PER_CATEGORY) v = MAX_PER_CATEGORY;
    setCounts((prev) => ({ ...prev, [cat]: v }));
    setErrors((prev) => ({ ...prev, counts: undefined }));
  };

  const validateCounts = () => {
    if (totalPeople < 1) {
      setErrors({ counts: "Please select at least 1 person in a category." });
      return false;
    }
    return true;
  };

  const updateAttendee = (idx, field, value) => {
    setAttendees((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setErrors((prev) => ({ ...prev, [`a${idx}_${field}`]: undefined }));
  };

  const validateAttendee = (idx) => {
    const a = attendees[idx];
    const newErrs = {};
    if (!a.firstName.trim()) newErrs[`a${idx}_firstName`] = "Required";
    if (!a.lastName.trim()) newErrs[`a${idx}_lastName`] = "Required";
    if (!a.email.trim()) newErrs[`a${idx}_email`] = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(a.email.trim())) newErrs[`a${idx}_email`] = "Invalid email";
    if (!a.phone.trim()) newErrs[`a${idx}_phone`] = "Required";
    if (Object.keys(newErrs).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrs }));
      return false;
    }
    return true;
  };

  const handleReceiptsChange = (e) => {
    const files = Array.from(e.target.files || []);
    const accepted = [];
    const rejected = [];
    let runningTotal = receipts.reduce((sum, f) => sum + f.size, 0);

    for (const f of files) {
      if (!ACCEPTED_RECEIPT_MIME.includes(f.type)) {
        rejected.push(`${f.name}: unsupported type`);
        continue;
      }
      if (f.size > MAX_RECEIPT_SIZE_MB * 1024 * 1024) {
        rejected.push(`${f.name}: exceeds ${MAX_RECEIPT_SIZE_MB}MB`);
        continue;
      }
      if (runningTotal + f.size > MAX_TOTAL_RECEIPT_SIZE_BYTES) {
        rejected.push(
          `${f.name}: would exceed ${MAX_TOTAL_RECEIPT_SIZE_MB}MB total`
        );
        continue;
      }
      accepted.push(f);
      runningTotal += f.size;
    }
    const combined = [...receipts, ...accepted].slice(0, MAX_RECEIPTS);
    setReceipts(combined);
    if (rejected.length > 0) {
      setErrors((prev) => ({ ...prev, receipts: rejected.join("; ") }));
    } else {
      setErrors((prev) => ({ ...prev, receipts: undefined }));
    }
    e.target.value = "";
  };

  const removeReceipt = (idx) => {
    setReceipts((prev) => prev.filter((_, i) => i !== idx));
  };

  const goNext = () => {
    if (isCountsStep) {
      if (!validateCounts()) return;
      buildAttendees();
      setStep(1);
      return;
    }
    if (isPersonStep) {
      if (!validateAttendee(currentPersonIndex)) return;
      setStep((s) => s + 1);
      return;
    }
  };

  const goPrev = () => {
    if (step > 0 && !isResultsStep) setStep((s) => s - 1);
  };

  const encodeReceipts = async () => {
    const out = [];
    for (const f of receipts) {
      out.push({
        filename: f.name,
        contentType: f.type,
        content: await fileToBase64(f),
      });
    }
    return out;
  };

  const submitAll = async () => {
    if (receipts.length === 0) {
      setErrors((prev) => ({ ...prev, receipts: "Please upload at least one payment receipt." }));
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      const encodedReceipts = await encodeReceipts();
      const response = await axios.post(
        BATCH_URL,
        { attendees, receipts: encodedReceipts },
        { headers: { "Content-Type": "application/json" } }
      );
      setResults(response.data);
      setStep(totalPeople + 2);
    } catch (err) {
      console.error("Batch submit error:", err);
      const serverMsg = err?.response?.data?.error || "An error occurred while submitting.";
      setErrors((prev) => ({ ...prev, submit: serverMsg }));
    } finally {
      setSubmitting(false);
    }
  };

  const retryPerson = async (idx) => {
    setRetryingIndex(idx);
    try {
      const encodedReceipts = await encodeReceipts();
      const response = await axios.post(
        BATCH_URL,
        { attendees: [attendees[idx]], receipts: encodedReceipts, skipAdminSummary: true },
        { headers: { "Content-Type": "application/json" } }
      );
      const retryResult = response.data?.results?.[0];
      if (retryResult) {
        setResults((prev) => {
          if (!prev) return prev;
          const nextResults = [...prev.results];
          nextResults[idx] = { ...retryResult, index: idx };
          return { ...prev, results: nextResults };
        });
      }
    } catch (err) {
      console.error("Retry error:", err);
    } finally {
      setRetryingIndex(null);
    }
  };

  // Close modal on Escape
  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, step, attendees, receipts, counts]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (isModalOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isModalOpen]);

  const categoryLabel = (value) =>
    CATEGORIES.find((c) => c.value === value)?.label || value;

  const renderProgress = () => {
    if (isResultsStep) return null;
    const current = step + 1;
    return (
      <div className="wiz-progress">
        <div className="wiz-progress-bar">
          <div
            className="wiz-progress-fill"
            style={{ width: `${((current) / totalWizardSteps) * 100}%` }}
          />
        </div>
        <div className="wiz-progress-label">
          Step {current} of {totalWizardSteps}
          {isPersonStep && attendees[currentPersonIndex] && (
            <> &mdash; Person {currentPersonIndex + 1}: {categoryLabel(attendees[currentPersonIndex].userType)}</>
          )}
          {isCountsStep && <> &mdash; Choose categories</>}
          {isReceiptsStep && <> &mdash; Payment receipt(s)</>}
        </div>
      </div>
    );
  };

  const renderCountsStep = () => (
    <div className="wiz-step">
      <h2 className="wiz-step-title">Choose an option</h2>
      <p className="wiz-step-help">
        Enter how many people will register in each category (max {MAX_PER_CATEGORY} per category).
        At least one person is required to continue.
      </p>
      <div className="wiz-counts">
        {CATEGORIES.map(({ value, label }) => (
          <div className="wiz-count-row" key={value}>
            <label htmlFor={`count-${value}`}>{label}</label>
            <div className="wiz-count-controls">
              <button
                type="button"
                className="wiz-count-btn"
                onClick={() => handleCountChange(value, (counts[value] || 0) - 1)}
                aria-label={`Decrease ${label}`}
              >
                &minus;
              </button>
              <input
                id={`count-${value}`}
                type="number"
                min="0"
                max={MAX_PER_CATEGORY}
                value={counts[value]}
                onChange={(e) => handleCountChange(value, e.target.value)}
              />
              <button
                type="button"
                className="wiz-count-btn"
                onClick={() => handleCountChange(value, (counts[value] || 0) + 1)}
                aria-label={`Increase ${label}`}
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="wiz-total">Total: <strong>{totalPeople}</strong> person(s)</div>
      {errors.counts && <div className="wiz-error">{errors.counts}</div>}
    </div>
  );

  const renderPersonStep = () => {
    const idx = currentPersonIndex;
    const a = attendees[idx] || emptyAttendee("");
    return (
      <div className="wiz-step">
        <h2 className="wiz-step-title">
          Person {idx + 1} of {totalPeople}
        </h2>
        <div className="wiz-step-subtitle">{categoryLabel(a.userType)}</div>
        <div className="wiz-form-grid">
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              value={a.firstName}
              onChange={(e) => updateAttendee(idx, "firstName", e.target.value)}
              placeholder="First Name"
            />
            {errors[`a${idx}_firstName`] && (
              <span className="wiz-field-error">{errors[`a${idx}_firstName`]}</span>
            )}
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              value={a.lastName}
              onChange={(e) => updateAttendee(idx, "lastName", e.target.value)}
              placeholder="Last Name"
            />
            {errors[`a${idx}_lastName`] && (
              <span className="wiz-field-error">{errors[`a${idx}_lastName`]}</span>
            )}
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={a.email}
              onChange={(e) => updateAttendee(idx, "email", e.target.value)}
              placeholder="example@gmail.com"
            />
            {errors[`a${idx}_email`] && (
              <span className="wiz-field-error">{errors[`a${idx}_email`]}</span>
            )}
          </div>
          <div className="form-group">
            <label>Phone *</label>
            <input
              type="tel"
              value={a.phone}
              onChange={(e) => updateAttendee(idx, "phone", e.target.value)}
              placeholder="+212600000000"
            />
            {errors[`a${idx}_phone`] && (
              <span className="wiz-field-error">{errors[`a${idx}_phone`]}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderReceiptsStep = () => {
    const totalBytes = receipts.reduce((sum, f) => sum + f.size, 0);
    const totalMB = totalBytes / (1024 * 1024);
    const usagePct = Math.min(100, (totalBytes / MAX_TOTAL_RECEIPT_SIZE_BYTES) * 100);
    const isFull =
      receipts.length >= MAX_RECEIPTS ||
      totalBytes >= MAX_TOTAL_RECEIPT_SIZE_BYTES;

    return (
    <div className="wiz-step">
      <h2 className="wiz-step-title">Payment receipt(s)</h2>
      <p className="wiz-step-help">
        Upload your payment receipt(s) for the {totalPeople} registration(s). PDF or image files are accepted.
        You can upload up to {MAX_RECEIPTS} files (max {MAX_RECEIPT_SIZE_MB}MB each, {MAX_TOTAL_RECEIPT_SIZE_MB}MB total).
      </p>
      <label
        className={`wiz-file-drop ${isFull ? "is-disabled" : ""}`}
        aria-disabled={isFull}
      >
        <input
          type="file"
          accept=".pdf,image/png,image/jpeg,image/jpg,image/webp,application/pdf"
          multiple
          onChange={handleReceiptsChange}
          disabled={isFull}
        />
        <span>
          {isFull ? "Upload limit reached" : "Click to select file(s)"}
        </span>
        <span className="wiz-file-drop-sub">
          {receipts.length}/{MAX_RECEIPTS} files &middot; {totalMB.toFixed(2)} / {MAX_TOTAL_RECEIPT_SIZE_MB}MB
        </span>
      </label>
      <div className="wiz-usage">
        <div className="wiz-usage-bar">
          <div
            className={`wiz-usage-fill ${usagePct >= 100 ? "is-full" : usagePct >= 85 ? "is-warn" : ""}`}
            style={{ width: `${usagePct}%` }}
          />
        </div>
      </div>
      {receipts.length > 0 && (
        <ul className="wiz-receipt-list">
          {receipts.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              <span className="wiz-receipt-name">{f.name}</span>
              <span className="wiz-receipt-size">
                {(f.size / 1024).toFixed(1)} KB
              </span>
              <button
                type="button"
                className="wiz-receipt-remove"
                onClick={() => removeReceipt(i)}
                aria-label={`Remove ${f.name}`}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}
      {errors.receipts && <div className="wiz-error">{errors.receipts}</div>}
      {errors.submit && <div className="wiz-error">{errors.submit}</div>}
      {submitting && (
        <div className="wiz-submitting-note">
          Sending {totalPeople} confirmation email(s)… this may take up to 30 seconds. Please don't close this window.
        </div>
      )}

      <div className="wiz-summary">
        <h3>Summary</h3>
        <ul>
          {attendees.map((a, i) => (
            <li key={i}>
              <strong>{a.firstName} {a.lastName}</strong> &mdash; {categoryLabel(a.userType)} &mdash; {a.email}
            </li>
          ))}
        </ul>
      </div>
    </div>
    );
  };

  const renderResultsStep = () => {
    const list = results?.results || [];
    const successCount = list.filter((r) => r.status === "success").length;
    const failedCount = list.length - successCount;
    return (
      <div className="wiz-step">
        <h2 className="wiz-step-title">Submission results</h2>
        <p className="wiz-step-help">
          {successCount} of {list.length} registration(s) sent successfully
          {failedCount > 0 && <>, {failedCount} failed</>}.
        </p>
        <ul className="wiz-result-list">
          {list.map((r, i) => {
            const a = attendees[i];
            return (
              <li key={i} className={`wiz-result-item ${r.status}`}>
                <div className="wiz-result-info">
                  <strong>{a?.firstName} {a?.lastName}</strong>
                  <span> &mdash; {categoryLabel(a?.userType)}</span>
                  <span className="wiz-result-email"> &mdash; {a?.email}</span>
                </div>
                <div className="wiz-result-status">
                  {r.status === "success" ? (
                    <span className="wiz-badge ok">Sent</span>
                  ) : (
                    <>
                      <span className="wiz-badge fail">Failed</span>
                      {r.error && <span className="wiz-result-error">{r.error}</span>}
                      <button
                        type="button"
                        className="wiz-retry-btn"
                        disabled={retryingIndex === i}
                        onClick={() => retryPerson(i)}
                      >
                        {retryingIndex === i ? "Retrying…" : "Retry"}
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div className="book-section">
      <div
        ref={patternRef}
        className={`cubic-pattern ${isVisible ? "active" : ""}`}
      ></div>

      <h1 className="book-title">
        Congress Registration <span className="highlight">FORM</span>
      </h1>

      <div className="book-intro">
        <h2>2nd VETEd AFRICA 2026 Congress</h2>
        <p>
          We invite you to register for the congress by filling out the registration form.
        </p>
        <p>Please follow these steps:</p>
        <ol>
          <li>
            <strong>Fill out the registration form</strong> and complete all required information.
            Be sure to attach your payment receipt.
          </li>
          <li>
            <strong>Confirm your registration:</strong> After submitting the form, a confirmation
            email will be sent to you. For any questions regarding registration, please contact our team.
          </li>
        </ol>
        <button type="button" className="start-button" onClick={openModal}>
          Start registration process
        </button>
      </div>

      {isModalOpen && (
        <div
          className="wiz-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) requestClose();
          }}
        >
          <div className="wiz-modal" role="dialog" aria-modal="true">
            <div className="wiz-header">
              <h2 className="wiz-header-title">Congress Registration</h2>
              <button
                type="button"
                className="wiz-close"
                onClick={requestClose}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {renderProgress()}

            <div className="wiz-body">
              {isCountsStep && renderCountsStep()}
              {isPersonStep && renderPersonStep()}
              {isReceiptsStep && renderReceiptsStep()}
              {isResultsStep && renderResultsStep()}
            </div>

            <div className="wiz-footer">
              {!isResultsStep && (
                <>
                  <button
                    type="button"
                    className="wiz-btn wiz-btn-secondary"
                    onClick={goPrev}
                    disabled={step === 0 || submitting}
                  >
                    Previous
                  </button>

                  {isReceiptsStep ? (
                    <button
                      type="button"
                      className="wiz-btn wiz-btn-primary"
                      onClick={submitAll}
                      disabled={submitting}
                    >
                      {submitting ? "Submitting…" : "Submit All"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="wiz-btn wiz-btn-primary"
                      onClick={goNext}
                      disabled={submitting}
                    >
                      Next
                    </button>
                  )}
                </>
              )}
              {isResultsStep && (
                <button
                  type="button"
                  className="wiz-btn wiz-btn-primary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Book;
